/**
 * Semáforo único del Centro de envíos: fusiona el semáforo de ENVÍO (validación de datos para la
 * etiqueta, ya existente) con el de FACTURA (semáforo pre-factura del backend, /api/prefactura).
 *
 * - Color final = el PEOR de los dos (factura rojo → rojo; ámbar o "en espera" → ámbar).
 * - Si la parte de factura no está disponible (sin permiso, error o cargando) la bandera es
 *   exactamente la de siempre: solo envío.
 * - Clic en la bandera → modal con el detalle PrestaShop vs Factusol y "Marcar revisado".
 */
import { ReactNode, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Loader2, CheckCircle2, AlertTriangle, XCircle, Clock, Info, Undo2 } from 'lucide-react'
import { prefactura, PrefacturaStatus, PrefacturaDetail } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

export type FlagStatus = 'green' | 'yellow' | 'red'

export interface ShippingValidation {
  status: FlagStatus
  issues: string[]
}

const RANK: Record<FlagStatus, number> = { green: 0, yellow: 1, red: 2 }

/** Color de la bandera aportado por la parte de factura (null = no altera la de envío). */
export function invoiceFlag(inv?: PrefacturaStatus | null): FlagStatus | null {
  if (!inv || inv.kind !== 'eval' || !inv.color) return null
  if (inv.color === 'red') return 'red'
  if (inv.color === 'amber' || inv.color === 'grey') return 'yellow'
  return 'green'
}

/** Peor de envío y factura. */
export function combineStatus(ship: ShippingValidation, inv?: PrefacturaStatus | null): FlagStatus {
  const f = invoiceFlag(inv)
  if (!f) return ship.status
  return RANK[f] > RANK[ship.status] ? f : ship.status
}

/** ¿Listo para facturar? (solo la parte de factura, independiente de los avisos de envío) */
export function isReadyToInvoice(inv?: PrefacturaStatus | null): boolean {
  return !!inv && inv.kind === 'eval' && inv.color === 'green'
}

const FLAG_BG: Record<FlagStatus, string> = {
  green: 'bg-green-500',
  yellow: 'bg-amber-500',
  red: 'bg-red-500',
}

const SEV_ICON = {
  red: <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />,
  grey: <Clock className="w-3.5 h-3.5 text-slate-300 shrink-0" />,
  amber: <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />,
  info: <Info className="w-3.5 h-3.5 text-sky-300 shrink-0" />,
}

function invoiceTooltipLines(inv: PrefacturaStatus | null | undefined, loading: boolean, failed: boolean) {
  if (loading) return <span className="text-slate-300">Calculando…</span>
  if (failed) return <span className="text-slate-300">No disponible (Factusol no responde)</span>
  if (!inv) return <span className="text-slate-300">Sin datos</span>
  if (inv.kind === 'facturado') return <span className="text-slate-300">✓ Facturado o albaranado en Factusol</span>
  if (inv.kind === 'anulado') return <span className="text-slate-300">Pedido anulado en Factusol</span>
  if (inv.kind !== 'eval') return <span className="text-slate-300">{inv.summary || 'Sin evaluar'}</span>
  if (inv.color === 'green') {
    return <span className="text-green-300">✓ {inv.reviewed ? 'Revisado: listo para facturar' : 'Listo para facturar'}</span>
  }
  const relevant = (inv.issues || []).filter(i => i.severity !== 'info').slice(0, 5)
  return (
    <ul className="space-y-0.5">
      {relevant.map((i, k) => (
        <li key={k} className="flex items-start gap-1">{SEV_ICON[i.severity]}<span>{i.msg}</span></li>
      ))}
      <li className="text-slate-400 pt-0.5">Clic en la bandera para ver el detalle</li>
    </ul>
  )
}

/**
 * Tooltip de la bandera pintado en <body> (portal, posición fija): la tabla de pedidos tiene
 * overflow-hidden y, con pocos pedidos, un tooltip absoluto quedaba recortado e ilegible.
 * Se abre debajo alineado a la derecha; si no cabe, encima; nunca se sale de la ventana.
 */
export function FlagTooltip({ content, children, className = '' }: { content: ReactNode; children: ReactNode; className?: string }) {
  const anchorRef = useRef<HTMLDivElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    if (!open || !anchorRef.current || !tipRef.current) return
    const a = anchorRef.current.getBoundingClientRect()
    const t = tipRef.current.getBoundingClientRect()
    const gap = 4, margin = 8
    let top = a.bottom + gap
    if (top + t.height > window.innerHeight - margin && a.top - gap - t.height >= margin) {
      top = a.top - gap - t.height
    }
    top = Math.max(margin, Math.min(top, window.innerHeight - t.height - margin))
    const left = Math.max(margin, Math.min(a.right - t.width, window.innerWidth - t.width - margin))
    setPos({ top, left })
  }, [open])

  return (
    <div
      ref={anchorRef}
      className={className}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => { setOpen(false); setPos(null) }}
    >
      {children}
      {open && createPortal(
        <div
          ref={tipRef}
          className="fixed z-[1000] pointer-events-none"
          style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
        >
          {content}
        </div>,
        document.body
      )}
    </div>
  )
}

interface CombinedFlagProps {
  shipping: ShippingValidation
  invoice?: PrefacturaStatus | null
  invoiceLoading?: boolean
  invoiceFailed?: boolean
  onOpenInvoice?: () => void
}

/** Bandera única Envío + Factura (mismo aspecto que la bandera de envío de siempre). */
export function CombinedFlag({ shipping, invoice, invoiceLoading = false, invoiceFailed = false, onOpenInvoice }: CombinedFlagProps) {
  const status = combineStatus(shipping, invoice)
  const canOpen = !!onOpenInvoice && !!invoice && invoice.kind === 'eval'
  return (
    <div
      className={canOpen ? 'cursor-pointer' : ''}
      onClick={canOpen ? (e) => { e.stopPropagation(); onOpenInvoice!() } : undefined}
    >
      <FlagTooltip
        className="relative flex items-center justify-center"
        content={
        <div className="bg-slate-900 text-white text-xs rounded-lg p-2 shadow-lg w-80 space-y-2">
          <div>
            <p className="font-medium mb-1">Envío</p>
            {shipping.issues.length === 0 ? (
              <span className="text-green-300">✓ Datos correctos</span>
            ) : (
              <ul className="space-y-0.5">
                {shipping.issues.map((issue, i) => (
                  <li key={i} className="flex items-center gap-1">
                    <span className="w-1 h-1 bg-current rounded-full" />
                    {issue}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t border-slate-700 pt-1.5">
            <p className="font-medium mb-1">Factura</p>
            {invoiceTooltipLines(invoice, invoiceLoading, invoiceFailed)}
          </div>
        </div>
        }
      >
        <div className={`w-4 h-5 ${FLAG_BG[status]} rounded-sm relative`}>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[4px] border-l-transparent border-r-transparent border-b-white dark:border-b-slate-800" />
        </div>
      </FlagTooltip>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// Modal de detalle
// ---------------------------------------------------------------------------------------------

const COLOR_LABEL: Record<string, { label: string; cls: string }> = {
  green: { label: 'Listo para facturar', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  amber: { label: 'Revisar', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  red: { label: 'No facturar', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  grey: { label: 'En espera', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
}

const MOTIVOS = [
  'Stock físico comprobado',
  'Comprobado en Factusol',
  'Dirección confirmada con el cliente',
  'NIF confirmado',
  'Importe ampliado correcto',
  'Otro',
]

const fmt = (v: any) => (v === null || v === undefined || v === '' ? '—' : typeof v === 'boolean' ? (v ? 'Sí' : 'No') : String(v))
const eur = (v: any) => (typeof v === 'number' ? `${v.toFixed(2)} €` : fmt(v))

const REGIMEN: Record<string, string> = { '0': 'Con IVA', '1': 'Sin IVA', '2': 'Intracomunitario', '3': 'Exportación' }
const VIES: Record<string, string> = {
  '7': 'Válido (VIES)', '11': 'Validado a mano', '4': 'INVÁLIDO', '5': 'VIES no disponible', '9': 'VIES bloqueado',
  '10': 'Error VIES', '8': 'Italia: sin verificar', '6': 'NIF español', '1': 'País no UE',
}

function CompareTable({ d }: { d: PrefacturaDetail }) {
  const c = d.compare || {}
  const rows: [string, string, string, string?][] = [
    ['Importe', `${eur(c.importe?.web)} cobrado${c.importe?.vale_dev ? ` + vale ${eur(c.importe.vale_dev)}` : ''}`, eur(c.importe?.factusol), `esperado ${eur(c.importe?.esperado)}`],
    ['IVA', c.iva?.web_cobro_iva ? 'Cobró IVA' : 'Sin IVA', REGIMEN[String(c.iva?.factusol_regimen)] || fmt(c.iva?.factusol_regimen),
      `ficha: ${REGIMEN[String(c.iva?.ficha_regimen)] || fmt(c.iva?.ficha_regimen)} · VIES: ${VIES[String(c.iva?.vies_codigo)] || 'sin registro'}`],
    ['NIF', fmt(c.nif?.web), fmt(c.nif?.factusol), `ficha: ${fmt(c.nif?.ficha)}`],
    ['Nombre', fmt(c.nombre?.web), fmt(c.nombre?.factusol), `ficha: ${fmt(c.nombre?.ficha)}`],
    ['Dirección', fmt(c.direccion?.web), fmt(c.direccion?.factusol)],
    ['CP / Población', `${fmt(c.cp?.web)} ${fmt(c.poblacion?.web)}`, `${fmt(c.cp?.factusol)} ${fmt(c.poblacion?.factusol)}`],
    ['Provincia', fmt(c.provincia?.web), fmt(c.provincia?.factusol)],
    ['País', `facturación ${fmt(c.pais?.facturacion)}`, '', `entrega ${fmt(c.pais?.entrega)}`],
    ['Estado web', fmt(c.estado_web?.nombre), ''],
    ['Forma de pago', fmt(c.forma_pago?.web), fmt(c.forma_pago?.factusol)],
  ]
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-slate-500 dark:text-slate-400 uppercase">
          <th className="text-left py-1.5 pr-2 font-medium w-28"></th>
          <th className="text-left py-1.5 pr-2 font-medium">PrestaShop</th>
          <th className="text-left py-1.5 pr-2 font-medium">Pedido Factusol</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
        {rows.map(([k, web, fs, extra]) => (
          <tr key={k} className="align-top">
            <td className="py-1.5 pr-2 font-medium text-slate-600 dark:text-slate-300">{k}</td>
            <td className="py-1.5 pr-2 text-slate-800 dark:text-slate-200 break-words">{web}</td>
            <td className="py-1.5 pr-2 text-slate-800 dark:text-slate-200 break-words">
              {fs}
              {extra && <div className="text-slate-400">{extra}</div>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function InvoiceCheckModal({ orderId, reference, onClose }: { orderId: number; reference?: string; onClose: () => void }) {
  const toast = useToast()
  const qc = useQueryClient()
  const [motivo, setMotivo] = useState(MOTIVOS[0])
  const [nota, setNota] = useState('')

  const q = useQuery({
    queryKey: ['prefactura-detail', orderId],
    queryFn: () => prefactura.detail(orderId),
    retry: 0,
  })

  const done = () => {
    qc.invalidateQueries({ queryKey: ['prefactura-status'] })
    qc.invalidateQueries({ queryKey: ['prefactura-detail', orderId] })
  }

  const reviewM = useMutation({
    mutationFn: () => prefactura.review(orderId, motivo, nota),
    onSuccess: () => { toast.success('Marcado como revisado'); done() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'No se pudo marcar como revisado'),
  })
  const unreviewM = useMutation({
    mutationFn: () => prefactura.unreview(orderId),
    onSuccess: () => { toast.success('Revisión deshecha'); done() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'No se pudo deshacer'),
  })

  const d = q.data
  const issues = (d?.issues || [])
  const canReview = !!d && d.color === 'amber'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={(e) => { e.stopPropagation(); onClose() }}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-slate-900 dark:text-white">Facturación del pedido {reference || `#${orderId}`}</h3>
            {d && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${COLOR_LABEL[d.color]?.cls}`}>
                {d.reviewed ? 'Revisado · listo' : COLOR_LABEL[d.color]?.label}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="p-5 space-y-5">
          {q.isLoading ? (
            <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
          ) : q.isError || !d ? (
            <p className="text-sm text-slate-500">
              {(q.error as any)?.response?.data?.detail || 'No se pudo cargar el detalle (el pedido puede haberse facturado ya).'}
            </p>
          ) : (
            <>
              <div>
                <p className="text-xs font-medium uppercase text-slate-500 mb-2">Avisos</p>
                {issues.length === 0 ? (
                  <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Todo correcto: listo para facturar</p>
                ) : (
                  <ul className="space-y-1.5">
                    {issues.map((i, k) => (
                      <li key={k} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <span className="mt-0.5">{{
                          red: <XCircle className="w-4 h-4 text-red-500" />,
                          grey: <Clock className="w-4 h-4 text-slate-400" />,
                          amber: <AlertTriangle className="w-4 h-4 text-amber-500" />,
                          info: <Info className="w-4 h-4 text-sky-500" />,
                        }[i.severity]}</span>
                        <span>{i.msg}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <p className="text-xs font-medium uppercase text-slate-500 mb-2">PrestaShop vs Factusol</p>
                <CompareTable d={d} />
              </div>

              {d.reviewed && d.review ? (
                <div className="flex items-center justify-between rounded-lg bg-green-50 dark:bg-green-900/20 px-4 py-3 text-sm">
                  <div className="text-green-800 dark:text-green-300">
                    Revisado por <b>{d.review.reviewed_by}</b> el {d.review.reviewed_at?.replace('T', ' ')} — {d.review.motivo}
                    {d.review.nota ? <span className="text-green-700/80"> · {d.review.nota}</span> : null}
                  </div>
                  <button
                    onClick={() => unreviewM.mutate()}
                    disabled={unreviewM.isPending}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-green-300 text-green-800 hover:bg-green-100 dark:text-green-300 dark:border-green-800"
                  >
                    <Undo2 className="w-3.5 h-3.5" /> Deshacer revisión
                  </button>
                </div>
              ) : canReview ? (
                <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-900/10 p-4 space-y-3">
                  {d.review_expired && (
                    <p className="text-xs text-amber-800 dark:text-amber-300">Había una revisión anterior, pero los avisos han cambiado: hay que volver a revisarlo.</p>
                  )}
                  <p className="text-sm text-slate-700 dark:text-slate-200">Si has comprobado los avisos y está bien para facturar, márcalo como revisado (vale mientras los avisos no cambien).</p>
                  <div className="flex flex-wrap gap-2">
                    <select value={motivo} onChange={(e) => setMotivo(e.target.value)}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm">
                      {MOTIVOS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota (opcional)"
                      className="flex-1 min-w-48 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm" />
                    <button
                      onClick={() => reviewM.mutate()}
                      disabled={reviewM.isPending || (motivo === 'Otro' && !nota.trim())}
                      className="px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-medium"
                    >
                      Marcar revisado
                    </button>
                  </div>
                </div>
              ) : d.color === 'red' ? (
                <p className="text-xs text-slate-500">Los avisos en rojo no se pueden marcar como revisados: hay que corregirlos (en Factusol o en la web) y pulsar Actualizar.</p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
